import axios from "axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const tokenStore = {
  get: () => sessionStorage.getItem("oh_token"),
  set: (t) => sessionStorage.setItem("oh_token", t),
  clear: () => sessionStorage.removeItem("oh_token"),
};
export const api = axios.create({ baseURL: API });
api.interceptors.request.use((c) => {
  const t = tokenStore.get();
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});
api.interceptors.response.use(
  (r) => r,
  (e) => {
    const url = e.config?.url || "";
    if (e.response?.status === 401 && tokenStore.get() && !url.includes("/auth/login") && !url.includes("creator")) {
      tokenStore.clear();
      window.location.replace("/");
    }
    return Promise.reject(e);
  }
);

export const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg?.replace("Value error, ", "")).join(" ");
  return String(d);
};

export const fileUrl = (id, thumb) => (id ? `${API}/files/${id}?auth=${tokenStore.get()}${thumb ? "&thumb=1" : ""}` : null);

export const useGet = (url, opts = {}) =>
  useQuery({
    queryKey: [url],
    queryFn: () => api.get(url).then((r) => r.data),
    refetchInterval: opts.interval ?? false,
    refetchOnWindowFocus: true,
    enabled: (opts.enabled ?? true) && !!url,
  });

export function useAct() {
  const qc = useQueryClient();
  return async (method, url, body, { ok, headers } = {}) => {
    try {
      const r = method === "delete" ? await api.delete(url, { headers }) : await api[method](url, body, { headers });
      qc.invalidateQueries();
      if (ok) toast.success(ok);
      return r.data;
    } catch (e) {
      toast.error(errMsg(e));
      throw e;
    }
  };
}

export async function uploadFile(file, purpose = "gallery", onProgress) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("purpose", purpose);
  const r = await api.post("/media", fd, { onUploadProgress: (p) => onProgress && onProgress(p.loaded / (p.total || 1)) });
  return r.data;
}

export async function captureFrame(src, time = 0.5) {
  return new Promise((resolve, reject) => {
    const v = document.createElement("video");
    v.muted = true;
    v.playsInline = true;
    v.preload = "auto";
    v.crossOrigin = "anonymous";
    v.src = src;
    v.onloadeddata = () => { v.currentTime = Math.min(time, (v.duration || 1) - 0.05); };
    v.onseeked = () => {
      const c = document.createElement("canvas");
      c.width = v.videoWidth;
      c.height = v.videoHeight;
      c.getContext("2d").drawImage(v, 0, 0);
      c.toBlob((b) => (b ? resolve(new File([b], "thumb.jpg", { type: "image/jpeg" })) : reject()), "image/jpeg", 0.85);
    };
    v.onerror = reject;
  });
}
